import { filter, includes } from 'lodash';
import React, { useState, useMemo } from 'react';
import styled from 'styled-components';

import {
  Header,
  Text,
  FlexCenter,
  FlexColumn,
  FlexRow,
  Icon,
  InfoIcon,
  Caution,
  IconMedallion,
  Pencil2,
  COLORS,
  ModalPopup,
  TextInput,
} from '@summer/ui-components';

import { Link } from 'components/shared/typography';
import { trackingEvents } from 'constants/trackingEvents';
import { track } from 'utils/track';

const nycEmployerData = [
  {
    agencyName: `Administration for Children's Services (ACS)`,
    employerName: 'The City of New York',
    employerContactEmailAddress: 'Verification.Unit@acs.nyc.gov',
    employerContactFirstName: `Administration for Children's Services`,
    employerContactLastName: 'Approver',
  },
  {
    agencyName: 'Human Resources Administration (HRA)',
    employerName: 'The City of New York',
    employerContactEmailAddress: 'EVU@dss.nyc.gov',
    employerContactFirstName: 'Human Resources Administration',
    employerContactLastName: 'Approver',
  },
  {
    agencyName: `Department of Social Services (DSS)`,
    employerName: 'The City of New York',
    employerContactEmailAddress: 'EVU@dss.nyc.gov',
    employerContactFirstName: 'Department of Social Services',
    employerContactLastName: 'Approver',
  },
  {
    agencyName: 'Department of Homeless Services (DHS)',
    employerName: 'The City of New York',
    employerContactEmailAddress: 'EVU@dss.nyc.gov',
    employerContactFirstName: 'Department of Homeless Services',
    employerContactLastName: 'Approver',
  },
  {
    agencyName: 'NYC Housing Authority (NYCHA)',
    employerName: 'NEW YORK CITY HOUSING AUTHORITY',
    employerContactEmailAddress: 'hr.evr@nycha.nyc.gov',
    employerContactFirstName: 'NYC Housing Authority',
    employerContactLastName: 'Approver',
  },
];

const filterAgencies = (agencyData, searchTerm) => {
  let filtered = agencyData;

  if (searchTerm.length > 0) {
    filtered = filter(agencyData, agency => {
      return includes(
        agency.agencyName.toLowerCase(),
        searchTerm.toLowerCase(),
      );
    });
  }
  return filtered;
};

const EmployerContactHelpModal = ({ isOpen, setShowHelpModal, formik }) => {
  const [agencySearchInputTerm, setAgencySearchInputTerm] = useState('');

  const filteredAgencies = useMemo(
    () => filterAgencies(nycEmployerData, agencySearchInputTerm),
    [agencySearchInputTerm],
  );

  return (
    <ModalPopup
      isOpen={isOpen}
      togglePopup={() => {
        setShowHelpModal(false);
        setAgencySearchInputTerm('');
      }}
      width={700}
      fullHeight="small"
      component={
        <FlexColumn>
          <ModalHeader direction="column">
            <IconMedallion
              SvgComponent={InfoIcon}
              size={32}
              color={COLORS.azure}
            />
            <Text size="small" bold color="secondary">
              THE CITY OF NEW YORK
            </Text>
            <Header h3>Contact Person</Header>
          </ModalHeader>
          <Divider />
          <SearchCaption paragraph>
            Search for your agency or department below. If your agency
            isn&rsquo;t listed, contact your Agency Personnel Officer (APO).
          </SearchCaption>
          <TextInput
            onChange={e => {
              setAgencySearchInputTerm(e.target.value);
            }}
            placeholder="Find your department..."
          />
          <AgencyListColumn>
            {filteredAgencies.length > 0 ? (
              filteredAgencies.map((agency, index) => {
                const {
                  agencyName,
                  employerName,
                  employerContactEmailAddress,
                  employerContactFirstName,
                  employerContactLastName,
                } = agency;
                const hasAgencyContactInfo =
                  !!employerContactEmailAddress &&
                  !!employerContactFirstName &&
                  !!employerContactLastName;

                return (
                  <>
                    <AgencyRow key={agency.agencyName}>
                      {hasAgencyContactInfo ? (
                        <>
                          <AgencyInfo>
                            <Text bold paragraph>
                              {agencyName}
                            </Text>
                            <Text paragraph>Employer Name: {employerName}</Text>
                            <Text paragraph>
                              Email Address: {employerContactEmailAddress}
                            </Text>
                          </AgencyInfo>
                          <Link
                            onClick={() => {
                              formik.setFieldValue(
                                'employerName',
                                employerName,
                              );
                              formik.setFieldValue(
                                'hrFirstName',
                                employerContactFirstName,
                              );
                              formik.setFieldValue(
                                'hrLastName',
                                employerContactLastName,
                              );
                              formik.setFieldValue(
                                'hrEmail',
                                employerContactEmailAddress,
                              );
                              setShowHelpModal(false);
                              track(
                                trackingEvents.PSLF
                                  .EMPLOYER_CONTACT_AUTOFILL_CLICKED,
                              );
                            }}
                          >
                            <FlexCenter direction="row">
                              <AutofillIcon
                                SvgComponent={Pencil2}
                                fill={COLORS.azure}
                                width={16}
                                height={16}
                              />
                              Autofill Form
                            </FlexCenter>
                          </Link>
                        </>
                      ) : (
                        <FlexColumn>
                          <Text bold>{agencyName}</Text>
                          <Text paragraph>
                            Please contact your Agency Personnel Officer (APO)
                            to help you identify your designated Public Service
                            Loan Forgiveness (PSLF) approver. This approver will
                            sign your form.
                          </Text>
                        </FlexColumn>
                      )}
                    </AgencyRow>
                    {index !== filteredAgencies.length - 1 && <Divider />}
                  </>
                );
              })
            ) : (
              <NoResultsContainer direction="column">
                <IconMedallion
                  SvgComponent={Caution}
                  size={64}
                  color={COLORS.azure}
                />
                <Header h3>No Results</Header>
                <Text paragraph>
                  We couldn&rsquo;t find any agencies matching{' '}
                  {agencySearchInputTerm}. Please contact your Agency Personnel
                  Officer (APO) for assistance.
                </Text>
              </NoResultsContainer>
            )}
          </AgencyListColumn>
        </FlexColumn>
      }
    />
  );
};

const AgencyListColumn = styled(FlexColumn)`
  width: 100%;
`;

const AgencyRow = styled(FlexRow)`
  justify-content: space-between;
  width: 100%;
  margin-bottom: 16px;
`;

const AgencyInfo = styled(FlexColumn)`
  max-width: 75%;
  margin-right: 16px;
`;

const NoResultsContainer = styled(FlexCenter)`
  & > * {
    margin-bottom: 8px;
  }
`;

const Divider = styled.div`
  width: 100%;
  border-bottom: 1px solid ${COLORS.grey};
  margin-top: 0px;
  margin-bottom: 16px;
`;

const ModalHeader = styled(FlexCenter)`
  & > * {
    margin-bottom: 8px;
  }
`;

const SearchCaption = styled(Text)`
  margin-bottom: 16px;
`;

const AutofillIcon = styled(Icon)`
  margin-right: 8px;
`;

export default EmployerContactHelpModal;
